import { Component, ViewEncapsulation } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import {
    ADMIN_MANAGE_PROSPECT_AUDIT,
    ADMIN_MANAGE_PROSPECT_ACTIVITY,
    ADMIN_MANAGE_PROSPECT_DETAILS,
    ADMIN_MANAGE_PROSPECT_MIGRATION,
    ADMIN_MANAGE_PROSPECT_USERS,
    ADMIN_PROSPECTS,
    atlas,
    ADMIN_MANAGE_PROSPECT_INSIGHTS
} from '@lsq-frontend/fasttrack-shared/routing-atlas';
import { DrApplicationService, SchemaTypes } from '@lsqlabs/dr-application-shared';
import { ProspectData, ProspectService, SchemaService } from '@lsq-frontend/remote-services/ar-app';
import {
    ClaimService,
    Color,
    forLifeOf,
    getDynamicRouteLink,
    getRouterLinkUrlById,
    Icons,
    LifecycleAware,
    Modal,
    ModalService,
    StatusColor
} from '@lsq-frontend/common-ui';
import { getRouterLinkById } from '@lsq-frontend/common-ui';
import { DashboardClaim } from '@lsq-frontend/remote-services/enterprise/common';
import { ProspectWatchersModalComponent } from '../modals/prospect-watchers/prospect-watchers-modal.component';
import { PopoverPlacement } from 'ngx-smart-popover';

@LifecycleAware()
@Component({
    selector: 'manage-prospect',
    templateUrl: './manage-prospect.component.html',
    styleUrls: ['./manage-prospect.component.scss'],
    encapsulation: ViewEncapsulation.None
})
export class ManageProspectComponent {
    public tabsConfig$ = [];
    public loading = true;
    public schemaType: string;
    public icons = Icons;
    private color = Color;
    private status: string;
    private statusDisplay;
    public readonly statusColor = StatusColor;
    public readonly processingColor = Color.Yellow;
    public tooltipPlacement = PopoverPlacement.Right;
    public tooltipText: string = 'Created, Inactive, Invited, Registered, Submitted, Migrated';
    public popoverTitle: string = 'Prospect Statuses';

    constructor(
        private _router: Router,
        private _activatedRoute: ActivatedRoute,
        private _prospectService: ProspectService,
        private _schemaService: SchemaService,
        private _arService: DrApplicationService,
        private _claimService: ClaimService,
        private _modalService: ModalService
    ) {
        this._activatedRoute.params.pipe(forLifeOf(this)).subscribe(async (params) => {
            await this.fetchProspect(params?.prospectId);
            this.setTabsConfig(params?.prospectId);
        });
    }

    public async fetchProspect(prospectId: string): Promise<void> {
        try {
            const getProspect = this._prospectService.getProspectDataById(prospectId);
            const prospectData = await getProspect.toPromise();
            if (!prospectData) {
                return;
            }
            this._arService.setProspect(prospectData);
            const getProspectSchema = this._schemaService.getProspectSchema(
                prospectData.prospectSchemaId
            );
            const schemaData = await getProspectSchema.toPromise();
            this._arService.setProspectSchema(schemaData[0]);
            this.schemaType = schemaData[0].schemaType;

            this.initStages();
        } catch (e) {
            this.navigateToProspectsPage();
        }
        this.loading = false;

        this.status = this._arService.getProspect()?.status;
        this.statusColor[this.status];
        this.statusDisplay = ProspectData.StatusEnum;
    }

    public initStages() {
        this._arService.initStages();
    }

    public setTabsConfig(prospectId: string): void {
        this.tabsConfig$ = [
            {
                label: 'Summary',
                routerLink: getDynamicRouteLink(
                    getRouterLinkUrlById(atlas, ADMIN_MANAGE_PROSPECT_DETAILS),
                    {
                        prospectId
                    }
                )
            }
        ];
        if (this.schemaType === SchemaTypes.ArProspect) {
            this.tabsConfig$.push({
                label: 'Migration',
                routerLink: getDynamicRouteLink(
                    getRouterLinkUrlById(atlas, ADMIN_MANAGE_PROSPECT_MIGRATION),
                    {
                        prospectId
                    }
                )
            });
        }
        if (
            (this.schemaType === SchemaTypes.ArProspect ||
                this.schemaType === SchemaTypes.ScfBuyerProspect) &&
            this._arService.getProspect()?.status != ProspectData.StatusEnum.Migrated
        ) {
            this.tabsConfig$.push({
                label: 'Users',
                routerLink: getDynamicRouteLink(
                    getRouterLinkUrlById(atlas, ADMIN_MANAGE_PROSPECT_USERS),
                    {
                        prospectId
                    }
                )
            });
        }
        this.tabsConfig$.push({
            label: 'Audit',
            routerLink: getDynamicRouteLink(
                getRouterLinkUrlById(atlas, ADMIN_MANAGE_PROSPECT_AUDIT),
                {
                    prospectId
                }
            )
        });
        if (
            this.schemaType === SchemaTypes.ScfBuyerProspect &&
            this._claimService.isOnSync(DashboardClaim.AdminViewProspectInsights)
        ) {
            this.tabsConfig$.push({
                label: 'Insights',
                routerLink: getDynamicRouteLink(
                    getRouterLinkUrlById(atlas, ADMIN_MANAGE_PROSPECT_INSIGHTS),
                    {
                        prospectId
                    }
                )
            });
        }
    }

    public navigateToProspectsPage(): void {
        // eslint-disable-next-line @typescript-eslint/no-floating-promises
        this._router.navigate(getRouterLinkById(atlas, ADMIN_PROSPECTS), {});
    }

    public getBusinessName(): string {
        return this._arService.getProspect()?.businessName;
    }

    public showWatchersModal() {
        this._modalService.showModal(
            new Modal({
                title: 'Manage Watchers',
                component: ProspectWatchersModalComponent
            })
        );
    }
    public watchersCount() {
        const count = this._arService.getProspect()?.watchers?.length ?? 0;
        return count >= 100 ? '99+' : count.toString();
    }
}
